/**
 * Post-level reactions on feed updates (like, celebrate, etc.).
 * Stores in feedReactions/{feedItemId} and userReactions subcollection.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  getDoc,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { REACTION_EMOJIS } from '../constants/reactions';
import './FeedReactionStrip.css';

export default function FeedReactionStrip({ feedItemId, reactionCounts = {}, onReactionUpdate }) {
  const { currentUser } = useAuth();
  const [myEmojiIndex, setMyEmojiIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!feedItemId || !currentUser?.uid) {
      setLoading(false);
      return;
    }
    const userRef = doc(db, 'feedReactions', feedItemId, 'userReactions', currentUser.uid);
    getDoc(userRef)
      .then((snap) => {
        if (snap.exists()) setMyEmojiIndex(snap.data().emojiIndex);
        else setMyEmojiIndex(null);
      })
      .catch(() => setMyEmojiIndex(null))
      .finally(() => setLoading(false));
  }, [feedItemId, currentUser?.uid]);

  async function toggleReaction(emoji) {
    if (!feedItemId || !currentUser?.uid) return;
    const emojiIndex = REACTION_EMOJIS.indexOf(emoji);
    if (emojiIndex === -1) return;
    const feedRef = doc(db, 'feedReactions', feedItemId);
    const userRef = doc(db, 'feedReactions', feedItemId, 'userReactions', currentUser.uid);

    if (myEmojiIndex === emojiIndex) {
      try {
        await deleteDoc(userRef);
        await updateDoc(feedRef, {
          [`reactionCounts.${emojiIndex}`]: increment(-1),
          updatedAt: serverTimestamp(),
        });
        setMyEmojiIndex(null);
        onReactionUpdate?.(feedItemId, emojiIndex, -1);
      } catch (err) {
        console.warn('[FeedReactionStrip] remove failed', err);
      }
      return;
    }

    try {
      if (myEmojiIndex !== null) {
        await deleteDoc(userRef);
        await updateDoc(feedRef, {
          [`reactionCounts.${myEmojiIndex}`]: increment(-1),
          updatedAt: serverTimestamp(),
        });
      }
      await setDoc(userRef, { emojiIndex });
      await setDoc(feedRef, { updatedAt: serverTimestamp() }, { merge: true });
      await updateDoc(feedRef, {
        [`reactionCounts.${emojiIndex}`]: increment(1),
        updatedAt: serverTimestamp(),
      });
      setMyEmojiIndex(emojiIndex);
      onReactionUpdate?.(feedItemId, emojiIndex, myEmojiIndex !== null ? 0 : 1);
    } catch (err) {
      console.warn('[FeedReactionStrip] add failed', err);
    }
  }

  if (!currentUser) return null;

  return (
    <div className="feed-reaction-strip" aria-label="React to this update">
      {REACTION_EMOJIS.map((emoji) => {
        const idx = REACTION_EMOJIS.indexOf(emoji);
        const count = Number(reactionCounts[idx]) || 0;
        const isActive = !loading && myEmojiIndex === idx;
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => toggleReaction(emoji)}
            className={`feed-reaction-btn ${isActive ? 'feed-reaction-btn-active' : ''}`}
            title={isActive ? 'Remove reaction' : 'React'}
            aria-pressed={isActive}
            disabled={loading}
          >
            <span className="feed-reaction-emoji">{emoji}</span>
            {count > 0 && <span className="feed-reaction-count">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
